import { startTransition, useCallback, useEffect, useState } from 'react';
import { mapSearchStateToAiSearchContext } from '@pkgs/components/utils/tracking';
import { getDefaultSortingOptionId, isNonEmptyObject } from '@pkgs/components/utils/searchStateUtils';
import { PageType, TemplateType } from '@pkgs/components/types/tracking';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { getConsentContext } from '@pkgs/components/hooks/useSearchModuleTracking';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { SortingOptionId } from '@pkgs/components/enums/enums';
import {
  getABTestContext,
  getAiSearchContext,
  getPageContext,
  getSearchContext,
  getShopContext,
  trackUserInteractionEvent,
  UserActionEnum,
} from '@ev-tech/snowplow-library';

import { Switch } from '../Switch/Switch';

export const SearchModeToggle = () => {
  const {
    t,
    tracking,
    countryCode,
    options: { language },
    featureFlags,
    smartSortPreferences,
  } = useAppContext();
  const searchMode = useSearchStore((state) => state.searchMode);
  const setSearchMode = useSearchStore((state) => state.setSearchMode);
  const masterDataShopIds = useSearchStore((state) => state.masterDataShopIds);
  const businessArea = useSearchStore((state) => state.objectTypeCategory);
  const preferences = useSearchStore((state) => state.preferences);
  const autoCompleteOptionCategory = useAutoCompleteStore((state) => state.autoCompleteOptionCategory);
  const inputText = useAutoCompleteStore((state) => state.inputText);
  const placeIds = useSearchStore((state) => state.placeIds);
  const isMapActive = useMapVisibilityStore((state) => state.isMapActive);
  const setSortingOptionById = useSearchStore((state) => state.setSortingOptionById);
  const marketingType = useSearchStore((state) => state.marketingType);
  const sortingOptionId = useSearchStore((state) => state.sortingOptionId);
  const [selectedSearchMode, setSelectedSearchMode] = useState(searchMode);
  const promptValue = usePromptStore((state) => state.promptValue);
  const hasPerformedAiSearch = promptValue.trim().length > 0;

  useEffect(() => {
    setSelectedSearchMode(searchMode);
  }, [searchMode]);

  const handleChange = useCallback(
    (isAiMode: boolean) => {
      const nextSearchMode = isAiMode ? SearchMode.AI : SearchMode.CLASSIC;
      setSelectedSearchMode(nextSearchMode);
      startTransition(() => {
        setSearchMode(nextSearchMode);

        if (nextSearchMode === SearchMode.CLASSIC && featureFlags.isSmartSortEnabled && isNonEmptyObject(smartSortPreferences)) {
          setSortingOptionById(SortingOptionId.SMART_SORT);
        } else if (nextSearchMode === SearchMode.AI && hasPerformedAiSearch && isNonEmptyObject(preferences)) {
          setSortingOptionById(SortingOptionId.PREFERENCES);
        } else if (
          nextSearchMode === SearchMode.AI &&
          hasPerformedAiSearch &&
          (sortingOptionId === SortingOptionId.SMART_SORT || sortingOptionId === SortingOptionId.PREFERENCES)
        ) {
          setSortingOptionById(getDefaultSortingOptionId(marketingType));
        } else if (nextSearchMode === SearchMode.CLASSIC) {
          setSortingOptionById(getDefaultSortingOptionId(marketingType));
        }
      });

      trackUserInteractionEvent({
        elementId: `ai-search_${isAiMode ? 'on' : 'off'}`,
        action: UserActionEnum.click,
        contexts: [
          getPageContext({
            isHQPage: true,
            type: PageType.SEARCH_RESULT,
            country_code: countryCode,
            page_language: language,
            template_type: tracking?.templateType ?? TemplateType.SEARCH_RESULT,
          }),
          getConsentContext(),
          getShopContext({ id: masterDataShopIds?.[0], name: businessArea }),
          getAiSearchContext(
            mapSearchStateToAiSearchContext({
              preferences,
              isAiSearchEnabled: nextSearchMode === SearchMode.AI,
            }),
          ),
          getSearchContext({ search_term_selected: inputText, category: autoCompleteOptionCategory, place_id: placeIds?.join(',') }),
          getABTestContext({
            id: 'isMapToggledOn',
            name: 'isMapToggledOn',
            variant: isMapActive ? 'true' : 'false',
            variant_description: 'map initially toggled on or off',
          }),
        ],
      });
    },
    [
      autoCompleteOptionCategory,
      businessArea,
      countryCode,
      inputText,
      isMapActive,
      smartSortPreferences,
      language,
      masterDataShopIds,
      placeIds,
      preferences,
      setSearchMode,
      sortingOptionId,
      tracking?.templateType,
      featureFlags.isSmartSortEnabled,
      hasPerformedAiSearch,
      marketingType,
      setSortingOptionById,
    ],
  );

  return (
    <Switch
      key={selectedSearchMode}
      label={t('search.aiSearch.aiMode.title')}
      isSelected={selectedSearchMode === SearchMode.AI}
      onChange={handleChange}
      data-testid="search-components_ai-switch"
      hasAiStyle
    />
  );
};
